import Close from "@mui/icons-material/Close";
import FilterList from "@mui/icons-material/FilterList";
import {
  Button,
  IconButton,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import { useCallback, useEffect, useMemo, useState } from "react";

interface WasteCodeFilterPopoverProps {
  open: boolean;
  anchorEl: HTMLButtonElement | null;
  filters: WasteCodeFilters;
  onClose: () => void;
  onApplyFilters: (newFilters: WasteCodeFilters) => void;
  onClearFilters: () => void;
  activeFilterCount: number;
}

export interface WasteCodeFilters {
  federalSearch: string;
  stateSearch: string;
}

export const WasteCodeFilterPopover = ({
  open,
  anchorEl,
  filters,
  onClose,
  onApplyFilters,
  onClearFilters,
  activeFilterCount,
}: WasteCodeFilterPopoverProps) => {
  const [pendingFilters, setPendingFilters] =
    useState<WasteCodeFilters>(filters);

  // Update pending filters when the prop changes (e.g., when cleared)
  useEffect(() => {
    setPendingFilters(filters);
  }, [filters]);

  const handlePendingFilterChange = useCallback(
    (field: keyof WasteCodeFilters, value: string) => {
      setPendingFilters((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  const handleApply = useCallback(() => {
    onApplyFilters(pendingFilters);
    onClose();
  }, [pendingFilters, onApplyFilters, onClose]);

  const handleClear = useCallback(() => {
    const clearedFilters = {
      federalSearch: "",
      stateSearch: "",
    };
    setPendingFilters(clearedFilters);
    onClearFilters();
    onClose();
  }, [onClearFilters, onClose]);

  const handleClose = useCallback(() => {
    // Reset pending filters to current applied filters when closing without applying
    setPendingFilters(filters);
    onClose();
  }, [filters, onClose]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleApply();
    }
  }, [handleApply]);

  // Calculate pending filter count for the Clear All button
  const pendingFilterCount = useMemo(() => {
    let count = 0;
    if (pendingFilters.federalSearch) count++;
    if (pendingFilters.stateSearch) count++;
    return count;
  }, [pendingFilters]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          sx: {
            width: 460,
            p: 3,
          },
        },
      }}
    >
      <Stack spacing={3}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <FilterList fontSize="small" />
            <Typography variant="h6">Filter by</Typography>
          </Stack>
          <IconButton onClick={handleClose} size="small">
            <Close fontSize="medium" />
          </IconButton>
        </Stack>

        <TextField
          label="Federal Hazardous Waste Codes"
          placeholder="Search federal waste codes..."
          value={pendingFilters.federalSearch}
          onChange={(e) =>
            handlePendingFilterChange("federalSearch", e.target.value)
          }
          onKeyDown={handleKeyDown}
          fullWidth
          size="small"
        />

        <TextField
          label="State Hazardous Waste Codes"
          placeholder="Search state waste codes..."
          value={pendingFilters.stateSearch}
          onChange={(e) =>
            handlePendingFilterChange("stateSearch", e.target.value)
          }
          onKeyDown={handleKeyDown}
          fullWidth
          size="small"
        />

        <Stack direction="row" justifyContent="space-between">
          <Button
            variant="text"
            onClick={handleClear}
            disabled={pendingFilterCount === 0}
          >
            Clear All
          </Button>
          <Button variant="contained" onClick={handleApply}>
            Apply
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
};
